
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useGame } from '../../../contexts/GameContext';
import { 
  Trophy, 
  Clock, 
  Target, 
  TrendingUp, 
  Users, 
  Brain,
  Award,
  Calendar,
  BarChart3,
  Percent
} from "lucide-react";
import { Progress } from "@/components/ui/progress";

const StatisticsTab: React.FC = () => {
  const { statistics, decisionHistory, completedChallenges } = useGame();

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const formatDate = (dateString: string): string => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return 'N/A';
    }
  };

  const successRate = statistics.totalDecisionsMade > 0 
    ? Math.round((statistics.successfulDecisions / statistics.totalDecisionsMade) * 100)
    : 0;

  const levelProgress: Record<string, number> = {
    'Junior': 20,
    'Mid-Level': 40,
    'Senior': 60,
    'Leader': 80,
    'Expert': 100,
  };

  const stats = [
    {
      title: "Total Decisions",
      value: statistics.totalDecisionsMade,
      icon: Target,
      color: "text-bitcoin-accent",
      description: "Career choices made"
    },
    {
      title: "Success Rate",
      value: `${successRate}%`,
      icon: Percent,
      color: "text-web3-green",
      description: "Successful outcomes"
    },
    {
      title: "Challenges Completed",
      value: statistics.challengesCompleted,
      icon: Trophy,
      color: "text-yellow-500",
      description: "Obstacles overcome"
    },
    {
      title: "Time Played",
      value: formatTime(statistics.totalTimePlayed),
      icon: Clock,
      color: "text-accent",
      description: "Total simulation time"
    },
    {
      title: "Sessions",
      value: statistics.sessionsPlayed,
      icon: Users,
      color: "text-purple-400",
      description: "Times played"
    },
    {
      title: "Skills Unlocked",
      value: statistics.skillsUnlocked,
      icon: Brain,
      color: "text-pink-400",
      description: "Abilities mastered"
    },
    {
      title: "Achievements",
      value: statistics.achievementsEarned,
      icon: Award,
      color: "text-orange-400",
      description: "Milestones reached"
    },
    {
      title: "Last Played",
      value: formatDate(statistics.lastPlayedDate),
      icon: Calendar,
      color: "text-muted-foreground",
      description: "Most recent session"
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card className="bg-gradient-to-r from-bitcoin-primary/20 to-bitcoin-secondary/20 border-bitcoin-accent/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-bitcoin-accent">
            <BarChart3 className="h-6 w-6" />
            Career Statistics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">
            Your overall performance across all simulation sessions.
          </p>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <Card key={index} className="bg-card/50 border-border/50 hover:border-bitcoin-accent/30 transition-colors">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-2">
                <stat.icon className={`h-5 w-5 ${stat.color}`} />
                <span className="text-sm text-muted-foreground">{stat.title}</span>
              </div>
              <p className="text-2xl font-bold text-foreground">{stat.value}</p>
              <p className="text-xs text-muted-foreground mt-1">{stat.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Highest Level Card */}
      <Card className="bg-card/50 border-border/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <TrendingUp className="h-5 w-5 text-web3-green" />
            Career Progression
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="flex justify-between mb-2">
              <span className="text-sm text-muted-foreground">Highest Level Achieved</span>
              <span className="text-sm font-medium text-bitcoin-accent">{statistics.highestLevel}</span>
            </div>
            <Progress value={levelProgress[statistics.highestLevel] || 0} className="h-2" />
          </div>
          
          <div>
            <p className="text-sm text-muted-foreground mb-2">Roles Explored</p>
            <div className="flex flex-wrap gap-2">
              {statistics.rolesPlayed.length > 0 ? (
                statistics.rolesPlayed.map((role) => (
                  <span 
                    key={role} 
                    className="px-3 py-1 bg-bitcoin-primary/20 text-bitcoin-accent rounded-full text-xs font-medium"
                  >
                    {role}
                  </span>
                ))
              ) : (
                <span className="text-muted-foreground text-sm">No roles played yet</span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Decision History */}
      <Card className="bg-card/50 border-border/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Target className="h-5 w-5 text-accent" />
            Recent Decisions
          </CardTitle>
        </CardHeader>
        <CardContent>
          {decisionHistory.length > 0 ? (
            <div className="space-y-3 max-h-60 overflow-y-auto">
              {decisionHistory.slice(-5).reverse().map((decision, index) => (
                <div 
                  key={index} 
                  className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg"
                >
                  <div className="w-2 h-2 rounded-full bg-bitcoin-accent mt-2 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-foreground">{decision.challenge}</p>
                    <p className="text-xs text-muted-foreground">{decision.decision}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">No decisions recorded yet. Start a simulation to build your history.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StatisticsTab;
